import type {
  ArrangementStatus,
  ArrangementStatusWord,
  AttendanceState,
  DashboardTileColumnVocabulary,
  DeploymentStatus,
  FillState,
  PresenceTally,
  TemplateListItemDto,
} from '@pams/shared';
import {
  ARRANGEMENT_STATUSES,
  ARRANGEMENT_STATUS_WORDS,
  FILL_WORDS,
  arrangementStatusWordOf,
  fillWordOf,
  presenceOf,
  statusesOfArrangementStatusWord,
} from '@pams/shared';
import type { StatusTone } from '@/theme/statusTone';
import {
  ARRANGEMENT_ARCHIVED_LABEL,
  ARRANGEMENT_STATUS_LABEL,
  ARRANGEMENT_STATUS_TONE,
  DEPLOYMENT_STATUS_LABEL,
  DEPLOYMENT_STATUS_TONE,
  FILL_WORD_LABEL,
  LINES_UNNAMED_ZERO_LABEL,
  PRESENCE_LABEL,
  TEMPLATE_STATE_LABEL,
  TEMPLATE_STATE_TONE,
  fillTone,
  type TemplateState,
} from './labels';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C5b §3] WHAT EVERY SCREEN SAYS ABOUT AN ORDER'S LIFE AND ITS FORCE — ONE MODULE.
 *
 *  A client saw `Partially Filled` beside `Filled: 19 of 19 · All Filled` on one screen, and the
 *  audit behind `docs/status-matrix.md` found twelve screens each indexing the label maps on their
 *  own: four read `closedShort` and four did not, so an order closed short at nobody named read
 *  `All Filled` on the inbox, the fill footer, the today tile and its drill, and the duty table;
 *  a zero ask was red here and neutral there; an archived order was `Archived`, `In the Archive`
 *  or nothing, depending on which page opened it.
 *
 *  ⚠️ `lib/labels.ts` HOLDS THE WORDS AND THIS FILE HOLDS THE RULES THAT PICK ONE. A screen calls a
 *  function here and never indexes a map — `statusVocabulary.test.ts` fails on a screen that does —
 *  so a rule changes in one place and every surface moves with it. The rules are the ones
 *  `docs/status-matrix.md` states above its Surfaces tables, and `statusMatrix.test.ts` reads every
 *  `web.*` WORD row of that document through these functions — the rows stating the server's own
 *  figures are the API matrix test's (`SURFACES_PINNED_BY_API`), and the two sets partition.
 *
 *  ⚠️ PURE AND UNRENDERED. Each answers `{label, tone}` (or a number) from DTO facts, so a matrix
 *  cell is one call and no DOM — the same split `fillPost.ts` and `fillPermissions.ts` make.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export type Presented = { label: string; tone: StatusTone };

/**
 * `n of m named` — the one spelling of a fill count, on every surface that states one.
 *
 * ⚠️ [amended C5b review] AND THAT WAS TRUE OF FEWER SURFACES THAN IT SAID. The Assign PA list's
 * Progress column (`8 of 12`), `ORDERS_TO_FILL`'s row (`TILE_COPY.namedOf`) and the fill screen's
 * `alloc-count` (a private copy in `components/fill/copy.ts`) each had their own; all three call
 * this now. ONE stated exception: the preview's force-matrix CELL (`PREVIEW_COPY.namedOf`,
 * `n of r`) sits under a column heading that already says what is counted, and its twin in the
 * post column (`PREVIEW_COPY.postCount`) states the word.
 */
export const namedOf = (named: number, allocated: number): string => `${String(named)} of ${String(allocated)} named`;

// ── The lifecycle ──────────────────────────────────────────────────────────────────

/**
 * ⚠️ THE LIFECYCLE WORD, WITH THE ARCHIVE AXIS FOLDED IN ONCE.
 *
 * [LIFECYCLE]: archive is an AXIS, not a status, so a renderer reading `status` alone calls an
 * archived draft `Draft`. An archived order reads `Cancelled` when it was withdrawn before it
 * happened — the archive writes that status, and it is the one an operator must not misread — and
 * `Archived` otherwise (filed, or a discarded draft). A live order reads its word, where
 * `ALLOCATION_IN_PROGRESS` is `Published` (decision 1): a workflow marker is not a fill claim.
 */
export const orderStatusWord = (order: { status: ArrangementStatus; archivedAt: string | null }): Presented => {
  if (order.archivedAt !== null && order.status !== 'CANCELLED') {
    return { label: ARRANGEMENT_ARCHIVED_LABEL, tone: 'neutral' };
  }
  const word = arrangementStatusWordOf(order.status);
  return { label: ARRANGEMENT_STATUS_LABEL[word], tone: ARRANGEMENT_STATUS_TONE[word] };
};

/** One duty's own status — `PENDING` and `ALLOCATED` both read `Published` ([C5b §3], D8). */
export const deploymentStatusWord = (status: DeploymentStatus): Presented => ({
  label: DEPLOYMENT_STATUS_LABEL[status],
  tone: DEPLOYMENT_STATUS_TONE[status],
});

/**
 * The status filter's options — WORDS, so the operator is never offered two options that read
 * the same. The value sent is the word; `statusFilterQuery` expands it before it leaves.
 */
export const statusFilterOptions = (): { value: ArrangementStatusWord; label: string }[] =>
  ARRANGEMENT_STATUS_WORDS.map((word) => ({ value: word, label: ARRANGEMENT_STATUS_LABEL[word] }));

/**
 * ⚠️ A WORD, EXPANDED TO THE STATUSES IT COVERS — `Published` finds `PUBLISHED` AND
 * `ALLOCATION_IN_PROGRESS`.
 *
 * A URL may still carry a raw status somebody shared before the words existed; it is read as the
 * word it now belongs to, so `?status=ALLOCATION_IN_PROGRESS` also finds `PUBLISHED` (recorded,
 * not hidden). Anything else is sent as it came, and the server's schema refuses it with a field
 * error rather than this function guessing.
 */
export const statusFilterQuery = (value: string): string | ArrangementStatus[] => {
  const status = ARRANGEMENT_STATUSES.find((candidate) => candidate === value);
  if (status === undefined) return value;
  const statuses = statusesOfArrangementStatusWord(arrangementStatusWordOf(status));
  /* A word covering ONE status is sent exactly as it always was — `?status=DRAFT` — so only
     `Published` changes what reaches the wire. */
  return statuses.length === 1 ? (statuses[0] ?? value) : statuses;
};

/**
 * The filter control's own value — the WORD a URL value belongs to, so a link still carrying a
 * raw status shows `Published` in the control rather than `ALLOCATION_IN_PROGRESS`.
 */
export const statusFilterWord = (value: string | undefined): ArrangementStatusWord | undefined => {
  const status = ARRANGEMENT_STATUSES.find((candidate) => candidate === value);
  return status === undefined ? undefined : arrangementStatusWordOf(status);
};

/**
 * ⚠️ THE STATUS TILE, FOLDED BY WORD. Listing `PUBLISHED` and `ALLOCATION_IN_PROGRESS` as two
 * rows once both read `Published` would print `Published 2` beside `Published 3` — two answers to
 * one question (D12). Every word keeps a row, zero included, as the six statuses did.
 */
export const ordersByStatusRows = (
  byStatus: Partial<Record<ArrangementStatus, number>>,
): { word: ArrangementStatusWord; label: string; tone: StatusTone; count: number }[] =>
  ARRANGEMENT_STATUS_WORDS.map((word) => ({
    word,
    label: ARRANGEMENT_STATUS_LABEL[word],
    tone: ARRANGEMENT_STATUS_TONE[word],
    count: statusesOfArrangementStatusWord(word).reduce((total, status) => total + (byStatus[status] ?? 0), 0),
  }));

// ── The force ──────────────────────────────────────────────────────────────────────

/** The three facts every fill word is decided from. `allocatedCount` is what a zero ask reads. */
export type FillFacts = { fillState: FillState; closedShort: boolean; allocatedCount: number };

/**
 * ⚠️ THE FILL WORD AND ITS TONE. `COMPLETE` reads `All Filled` only without `closedShort` — which
 * `fillOfTotals` makes imply `named >= allocated > 0` — and `Closed short` with it; nobody named
 * is red only where something was asked (`fillTone`).
 */
export const fillBadge = (facts: FillFacts): Presented => ({
  label: FILL_WORD_LABEL[fillWordOf(facts.fillState, facts.closedShort)],
  tone: fillTone(facts),
});

/** `n of m named · <fill word>` — the list's Filled column, the record's strip, the bell. */
export const fillSummary = (facts: FillFacts & { namedCount: number }): Presented => {
  const badge = fillBadge(facts);
  return { label: `${namedOf(facts.namedCount, facts.allocatedCount)} · ${badge.label}`, tone: badge.tone };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C6a] THE BELL'S FORCE CLAUSE — `n of m named` and its dot — OR NOTHING AT ALL.
 *
 *  A bell entry is outstanding WORK, and since C6a a command role asked of a division is work the
 *  force figures know nothing about: an order whose force is finished (COMPLETE, or closed short)
 *  stays on the bell while its In-charge is awaited, and an order that asked a division for an
 *  In-charge and nothing else arrives with no force at all (`allocatedCount` 0). In both the force
 *  says nothing the operator must act on, so it says nothing — the entry states the awaited role
 *  alone, and `0 of 12 named · All Filled` (S07's force beside an awaited In-charge) is a sentence
 *  this function cannot produce rather than one a renderer must remember not to.
 *
 *  ⚠️ `allocatedCount` 0 HIDES THE CLAUSE ONLY BESIDE AN AWAITED ROLE. The server's `forceOpen` is
 *  "a share exists and the force is not COMPLETE" — so a force-only entry at allocated 0 (R16's
 *  legacy share, which never settles) is force work to it, counted in `forceTotal`. Hiding its clause
 *  would leave an entry giving no reason at all, and the bell's "N more" subtraction — which calls
 *  this — one short. It keeps `0 of 0 named · Nobody named`, neutral (D15), as before C6a. What stays
 *  inexact is the one shape the entry cannot distinguish: that legacy share AND an awaited role on one
 *  order, which the wire reads exactly as a command-only order (both `allocatedCount` 0, NOT_NAMED).
 *  The exact answer is a server `forceOpen` per entry.
 *
 *  ⚠️ [C6a review] `commandOpen` IS A BOOLEAN THE CALLER COMPUTES, NEVER THE ENTRY'S COMMAND FIELD.
 *  This file counts the force for every status surface, so it stays off the command vocabulary
 *  (`commandIsolation.test.ts` (i) names it); `NotificationBell` reads `commandAwaited` for emptiness
 *  and hands over only the answer — a yes/no, never the roles and never a figure.
 *
 *  ⚠️ THE BADGE READS THE ENTRY'S OWN `closedShort`. [C5b §3] passed `false` here because COMPLETE
 *  could not reach the bell; it can now, so a hard-coded `false` would be a live lie the moment the
 *  gate above were ever relaxed. The matrix's `web.bell` reader calls this function.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const bellForceClause = (
  entry: FillFacts & { namedCount: number },
  commandOpen: boolean,
): { count: string; badge: Presented } | null =>
  entry.fillState !== 'COMPLETE' && (entry.allocatedCount > 0 || !commandOpen)
    ? { count: namedOf(entry.namedCount, entry.allocatedCount), badge: fillBadge(entry) }
    : null;

/**
 * ⚠️ THE FILL SCREEN'S FOOTER. The count is the CLIENT's staffable slots (vans excluded, staged
 * names included) and the word and tone are the SERVER's — the sheet's own `closedShort`, which
 * this footer never read: it printed `All Filled` for every COMPLETE sheet, so a division that
 * closed short at nobody named read `🔒 All Filled · 0 of 12 named` (S07).
 */
export const fillFooter = (
  sheet: FillFacts,
  named: number,
  total: number,
): { locked: boolean; word: string | null; count: string; tone: StatusTone } => {
  const locked = sheet.fillState === 'COMPLETE';
  return {
    locked,
    word: locked ? fillBadge(sheet).label : null,
    count: namedOf(named, total),
    tone: fillTone(sheet),
  };
};

/**
 * ⚠️ A DUTY POINT'S TWO FIGURES — what it still LACKS and what THIS viewer still owes — and each is
 * ABSENT at zero rather than a `0 yours` chip on every row ([C5 §8]). Never merged into one.
 */
export const pointOutstanding = (
  preview: { unfilledCount: number; yoursUnfilled: number | null } | null | undefined,
): { stillToName: number | null; yours: number | null } => {
  const unfilled = preview?.unfilledCount ?? 0;
  const yours = preview?.yoursUnfilled ?? 0;
  return { stillToName: unfilled > 0 ? unfilled : null, yours: yours > 0 ? yours : null };
};

/**
 * ⚠️ THE INBOX'S STATUS FILTER. Filtering on `COMPLETE` finds BOTH of its causes, so the option
 * says so — a filter reading `All Filled` that returned a share closed short at nobody named would
 * be the defect this module exists to remove, arriving through the one control that narrows.
 */
export const fillFilterLabel = (state: FillState): string =>
  state === 'COMPLETE'
    ? `${FILL_WORD_LABEL.COMPLETE} or ${FILL_WORD_LABEL.CLOSED_SHORT}`
    : FILL_WORD_LABEL[fillWordOf(state, false)];

/**
 * ⚠️ A POST'S DOT ON THE FILL SCREEN. A post never reaches COMPLETE (`postFillStateOf` takes no
 * completion), so this is `fillTone` with no second cause — red only where the post asks for people.
 */
export const postDotTone = (summary: { fillState: FillState; allocatedCount: number }): StatusTone =>
  fillTone({ fillState: summary.fillState, closedShort: false, allocatedCount: summary.allocatedCount });

/**
 * ⚠️ THE RED COUNT'S SENTENCE, AND A RANGE THAT ASKED FOR NOBODY IS NOT "EVERY LINE IS NAMED".
 * Zero unnamed over a real ask is a finished job (`success`); zero over no ask is nothing to do
 * (`neutral`) — the dashboard's own twin of `isUnanswered` (D15).
 */
export const linesUnnamedFigure = (data: {
  unnamedSlots: number;
  namedSlots: number;
  allocatedSlots: number;
}): Presented => {
  if (data.unnamedSlots > 0) {
    return {
      label: `${data.namedSlots.toLocaleString('en-IN')} named of ${String(data.allocatedSlots)}`,
      tone: 'danger',
    };
  }
  return data.allocatedSlots > 0
    ? { label: LINES_UNNAMED_ZERO_LABEL.allNamed, tone: 'success' }
    : { label: LINES_UNNAMED_ZERO_LABEL.noAsk, tone: 'neutral' };
};

/**
 * ⚠️ A DRILL-TABLE CELL THE SERVER SENT AS A WORD KEY, IN ENGLISH. The server names the column's
 * vocabulary and sends the key (`PUBLISHED`, `CLOSED_SHORT`); a column with no vocabulary, or a
 * key this build does not know, prints as sent rather than as a blank.
 */
export const vocabularyWord = (vocabulary: DashboardTileColumnVocabulary | undefined, value: string): string => {
  if (vocabulary === 'ARRANGEMENT_STATUS_WORD') {
    const word = ARRANGEMENT_STATUS_WORDS.find((candidate) => candidate === value);
    return word === undefined ? value : ARRANGEMENT_STATUS_LABEL[word];
  }
  if (vocabulary === 'FILL_WORD') {
    const word = FILL_WORDS.find((candidate) => candidate === value);
    return word === undefined ? value : FILL_WORD_LABEL[word];
  }
  return value;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §5a] ATTENDANCE IN THREE WORDS — `presenceOf`'s reduction, worded here and nowhere else.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * One officer's word — the fill chip's. `null` where nothing is said: silence before the duty
 * starts, and a supervisor's exemption (FR-5.4.7).
 */
export const presenceWord = (state: AttendanceState, dutyStarted: boolean): string | null => {
  const presence = presenceOf(state, dutyStarted);
  return presence === null ? null : PRESENCE_LABEL[presence];
};

/**
 * ⚠️ `7 present · 2 absent · 1 not arrived` — THE ONE ATTENDANCE LINE, for the preview, the order view
 * and the printed order (§6), so the three cannot word one tally three ways.
 *
 * ⚠️ ALL THREE TERMS, ALWAYS, IN ONE ORDER, AND A ZERO IS STATED. §6c: a printed order is photocopied
 * and printed mono, where three tints collapse into one grey, so the TEXT carries the meaning — and a
 * term that vanished at zero would read as a state nobody counted. No tone is returned: this is words.
 */
export const presenceLine = (tally: PresenceTally): string =>
  [
    `${String(tally.present)} ${PRESENCE_LABEL.PRESENT.toLowerCase()}`,
    `${String(tally.absent)} ${PRESENCE_LABEL.ABSENT.toLowerCase()}`,
    `${String(tally.notArrived)} ${PRESENCE_LABEL.NOT_ARRIVED.toLowerCase()}`,
  ].join(' · ');

// ── A template's state ([P7B.1 §3d]) ─────────────────────────────────────────────────

/**
 * ⚠️ [P7B.1] WHAT A TEMPLATE IS, ON THE LIBRARY'S SCREEN: ALREADY CREATED, ELSE ITS READINESS.
 *
 * Created is the run guard's own rule — a LIVE order it produced (`archivedAt === null`), so a
 * discarded draft does not count and the template reads as its readiness again. It does not replace
 * the readiness: a created template that is also blocked still says why, and only a READY one may be
 * planned (`readiness.state`, the server's, decides the checkbox — never this word).
 */
export const templateStateOf = (item: Pick<TemplateListItemDto, 'readiness' | 'produced'>): TemplateState =>
  item.produced.some((order) => order.archivedAt === null) ? 'CREATED' : item.readiness.state;

/** A state's word and tone — for a count by state, where there is no one template to ask. */
export const templateStateWordOf = (state: TemplateState): Presented => ({
  label: TEMPLATE_STATE_LABEL[state],
  tone: TEMPLATE_STATE_TONE[state],
});

export const templateStateWord = (item: Pick<TemplateListItemDto, 'readiness' | 'produced'>): Presented =>
  templateStateWordOf(templateStateOf(item));
